import { useEffect, useRef, useState } from 'react'
import Filmstrip from '../Filmstrip'
import Confetti from '../Confetti'
import { updateParty } from '../../utils/partyGame'
import { DCDirectorBanner, DCHostDirectorLine } from './PartyDCIdentity'
import {
  IconTrophy, IconCrown, IconStar, IconReplay,
} from '../icons'

/**
 * Director's Cut — Phase 4: Results.
 *
 * Logic (unchanged in this presentation pass):
 *   - Rank agents by agentPoints. Director gets nothing.
 *   - If a single top scorer → automatic Agent of the Year.
 *   - If tied → Director's phone gets a tie-breaker prompt to choose the winner.
 *   - Once dcWinner is set, all devices show the final celebration with confetti.
 */
export default function PartyDCDone({
  party, currentUserId, forHost,
  onPlayAgain, onBackToLobby, onEndParty,
}) {
  const players      = party?.players ?? {}
  const directorUid  = party?.directorUid
  const dcChain      = Array.isArray(party?.dcChain) ? party.dcChain : []
  const points       = party?.agentPoints ?? {}
  const dcWinner     = party?.dcWinner
  const isDirector   = currentUserId === directorUid

  // Ranked agents
  const ranked = Object.entries(players)
    .filter(([uid]) => uid !== directorUid)
    .map(([uid, p]) => ({ uid, player: p, score: points[uid] ?? 0 }))
    .sort((a, b) => b.score - a.score)
  const topScore = ranked.length ? ranked[0].score : 0
  const tied = ranked.filter((r) => r.score === topScore && topScore > 0)
  const needsTiebreak = !dcWinner && tied.length > 1

  // Standard competition ranking off star score: tied agents share the higher
  // rank and the next distinct score skips (e.g. 4-3-3 → #1, #2, #2, then #4).
  const rankOfScore = (s) => 1 + ranked.filter((r) => r.score > s).length
  const isTieScore  = (s) => ranked.filter((r) => r.score === s).length > 1

  // ── Auto-resolve when there is a single top scorer ─────────────────────
  const autoResolvedRef = useRef(false)
  useEffect(() => {
    if (autoResolvedRef.current || dcWinner || tied.length !== 1) return
    if (!forHost) return
    autoResolvedRef.current = true
    updateParty(party.id, { dcWinner: tied[0].uid }).catch(() => {})
  }, [forHost, dcWinner, tied, party?.id])

  // ── Celebration confetti when winner is known ──────────────────────────
  const [celebrating, setCelebrating] = useState(false)
  useEffect(() => {
    if (!dcWinner) return
    setCelebrating(true)
    const tid = setTimeout(() => setCelebrating(false), 4500)
    return () => clearTimeout(tid)
  }, [dcWinner])

  const winnerEntry = dcWinner ? ranked.find((r) => r.uid === dcWinner) : null
  const iWon = !forHost && !isDirector && dcWinner === currentUserId

  // ── Tie-breaker: Director picks winner ─────────────────────────────────
  const handleTieBreak = async (uid) => {
    if (!isDirector || !party?.id) return
    await updateParty(party.id, { dcWinner: uid }).catch(() => {})
  }

  // ── DIRECTOR tie-breaker prompt (player phone) ─────────────────────────
  if (!forHost && needsTiebreak && isDirector) {
    return (
      <div className="dc-done">
        <h2
          className="dc-done__title"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}
        >
          <IconTrophy size={24} aria-hidden="true" />
          <span>You decide the Agent of the Year!</span>
        </h2>
        <p className="dc-done__sub">It's a tie. Your call.</p>
        <div className="dc-done__tie-grid">
          {tied.map(({ uid, player, score }) => {
            const initial = (player?.username || '?').charAt(0).toUpperCase()
            return (
              <button
                key={uid}
                type="button"
                className="dc-done__tie-card"
                onClick={() => handleTieBreak(uid)}
              >
                <div className="dc-done__tie-avatar">
                  {player?.avatar
                    ? <img src={player.avatar} alt="" />
                    : <span className="party-avatar__initial">{initial}</span>}
                </div>
                <span className="dc-done__tie-name">{player?.username || 'Guest'}</span>
                <span className="dc-done__tie-score">
                  <IconStar size={14} aria-hidden="true" />
                  <span>{score}</span>
                </span>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // ── HOST results ───────────────────────────────────────────────────────
  if (forHost) {
    return (
      <div className="dc-done dc-done--host">
        {celebrating && <Confetti />}

        <h1 className="dc-done__title">That's a wrap!</h1>
        <DCHostDirectorLine party={party} />

        {winnerEntry && (
          <div className="dc-done__winner-spotlight">
            <div className="dc-done__winner-trophy" aria-hidden="true">
              <IconTrophy size={28} />
            </div>
            <p className="dc-done__winner">
              Agent of the Year — <strong>{winnerEntry.player?.username || 'Guest'}</strong>
            </p>
          </div>
        )}

        {needsTiebreak && (
          <p className="dc-done__sub dc-done__sub--waiting">
            <span className="party-ready-dot" aria-hidden="true" />
            <span>Tie — waiting for the Director to decide...</span>
          </p>
        )}

        <div className="dc-done__leaderboard">
          {ranked.map(({ uid, player, score }, i) => {
            const isWinner = uid === dcWinner
            const initial = (player?.username || '?').charAt(0).toUpperCase()
            return (
              <div
                key={uid}
                className={`dc-done__lb-row${isWinner ? ' dc-done__lb-row--winner' : ''}`}
              >
                <span className="dc-done__lb-rank">
                  {isWinner
                    ? <span className="dc-done__lb-crown" aria-label="Winner"><IconCrown size={16} /></span>
                    : `#${rankOfScore(score)}`}
                </span>
                <div className="dc-done__lb-avatar">
                  {player?.avatar
                    ? <img src={player.avatar} alt="" />
                    : <span className="party-avatar__initial">{initial}</span>}
                </div>
                <span className="dc-done__lb-name">{player?.username || 'Guest'}</span>
                {!isWinner && isTieScore(score) && (
                  <span className="dc-done__lb-tie">(Tie)</span>
                )}
                <span className="dc-done__lb-score">
                  <IconStar size={14} aria-hidden="true" />
                  <span>{score}</span>
                </span>
              </div>
            )
          })}
        </div>

        {dcChain.length > 0 && (
          <div className="dc-done__chain">
            <p className="dc-done__chain-label">THE FINAL CUT</p>
            <Filmstrip chain={dcChain} onItemClick={() => {}} hidePlaceholder />
          </div>
        )}

        <div className="dc-done__actions">
          {onPlayAgain && (
            <button className="pb-action-btn pb-action-btn--primary" onClick={onPlayAgain}>
              <IconReplay size={18} aria-hidden="true" />
              <span>Play Again</span>
            </button>
          )}
          {onBackToLobby && (
            <button className="pb-action-btn pb-action-btn--outline" onClick={onBackToLobby}>
              Back to Lobby
            </button>
          )}
          {onEndParty && (
            <button className="pb-action-btn pb-action-btn--muted" onClick={onEndParty}>
              End Party
            </button>
          )}
        </div>
      </div>
    )
  }

  // ── PLAYER results ─────────────────────────────────────────────────────
  const myScore = points[currentUserId] ?? 0
  const myRank = rankOfScore(myScore)
  const myNominationsInChain = dcChain.filter(
    (c) => Array.isArray(c.nominators) && c.nominators.includes(currentUserId)
  )

  return (
    <div className="dc-done">
      {celebrating && <Confetti />}
      {isDirector && <DCDirectorBanner />}
      <h2
        className="dc-done__title"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}
      >
        {iWon ? (
          <>
            <IconTrophy size={24} aria-hidden="true" />
            <span>Agent of the Year</span>
          </>
        ) : isDirector
          ? "Director's Cut"
          : myRank > 0
          ? `#${myRank}${isTieScore(myScore) ? ' (Tie)' : ''} — Nice work!`
          : "That's a wrap!"}
      </h2>
      {!isDirector && (
        <p className="dc-done__my-score">
          You scored{' '}
          <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <IconStar size={14} aria-hidden="true" />
            <span>{myScore}</span>
          </strong>
        </p>
      )}
      {!isDirector && myNominationsInChain.length > 0 && (
        <>
          <p className="dc-done__sub">Your picks that made it:</p>
          <ul className="dc-done__my-picks">
            {myNominationsInChain.map((c, i) => (
              <li key={i}>{c.type === 'movie' ? c.title : c.name}</li>
            ))}
          </ul>
        </>
      )}
      {winnerEntry && !iWon && (
        <p className="dc-done__winner-note">
          Winner: {winnerEntry.player?.username || 'Guest'} (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle' }}>
            <IconStar size={12} aria-hidden="true" />
            <span>{winnerEntry.score}</span>
          </span>
          )
        </p>
      )}
      <p className="dc-done__waiting">Waiting for host...</p>

      <button className="pb-action-btn pb-action-btn--outline" onClick={onBackToLobby} style={{ marginTop: 16 }}>
        Back to Lobby
      </button>
    </div>
  )
}
